import axios from 'axios';
import {globalActions} from '../../store/global-actions';
import {advertActions} from '../../store/advert-actions';
import utils from '../../shared/utils';
import {formValidationMixin} from '../mixins/component-mixins';
import {defineRule, Field, Form} from 'vee-validate';
import { max, min } from '@vee-validate/rules';
import { CascadeName } from '../../components/modals/cascade-modal-constants'

defineRule('min', min);
defineRule('max', max);

const buttonCardActionTemplateName = '#button-card-action';
const primaryTextAction = '#primary-text-action';

const cardActionBase = {
    props: {
        modelValue: {},
        buttonClass: {},
        isEngageAction: {},
        engageLabelClass: {
            default: 'btn__icon-label',
        },
        isMobile: {
            type: Boolean,
            required: false,
        },
    },
    data() {
        return Object.assign(this.modelValue, {isMobileDevice: this.isMobile});
    },
    emits: ['set-primary', 'change'],
    methods: {
        setPrimary() {
            this.$emit('set-primary', this.modelValue);
        },
        change() {
            this.modelValue.state = this.state;
            this.$emit('change', this.modelValue);
        },
    },
};

const buttonCardAction = {
    extends: cardActionBase,
    template: buttonCardActionTemplateName,
};

const stateCardActionMixin = {
    data() {
        return {state: this.modelValue.state};
    },
    computed: {
        currentState() {
            return this.state;
        },
    },
    methods: {
        sendChangeStateRequest() {
            return axios.post(this.url)
                .then((result) => {
                    this.state = result.data;
                    this.change();
                })
                .catch((result) => globalActions.handleAjaxErrors(result.response));
        },

        changeState() {
            this.sendChangeStateRequest();
        },
    },
};

const primaryTextActionBase = {
    extends: cardActionBase,
    template: primaryTextAction,
    mixins: [formValidationMixin],
    components: {
        ValidationField: Field,
        VeeValidationFormContext: Form,
    },
    props: {
        forceFocus: {},
    },
    data() {
        return {
            uniqueId: utils.genUid(),
            validationExpression: null,
            message: null,
            showErrors: false,
        };
    },
    computed: {
        validationModel() {
            return this.message || {};
        },
    },
    watch: {
        forceFocus(value) {
            if (value) {
                this.focusTextarea();
            }
        },
    },
    methods: {
        focusTextarea() {
            this.$refs.textarea.focus();
        },
        clearTextarea() {
            this.$refs.textarea.clear();
        },
    },
};

function initCardActionComponents(app) {
    app.component('card-action', {
        extends: cardActionBase,
        template: '#card-action',
    });

    app.component('state-card-action', {
        extends: cardActionBase,
        template: '#state-card-action',
        mixins: [stateCardActionMixin],
    });

    app.component('visibility-card-action', {
        extends: cardActionBase,
        template: '#state-card-action',
        mixins: [stateCardActionMixin],
        methods: {
            changeState() {
                if (this.state === false) {
                    this.sendChangeStateRequest();
                } else {
                    advertActions.openCascadeModal(this.modelValue, CascadeName.HIDE_CASCADE);
                }
            },
        },
    });

    app.component('fullscreen-card-action', {
        extends: buttonCardAction,
        props: {
            isAdvert: {
                type: Boolean,
                default: true,
            },
        },
        methods: {
            onActionClick() {
                globalActions.openFullscreenModal(this.url, this.isAdvert);
            },
        },
    });

    app.component('share-card-action', {
        extends: cardActionBase,
        template: '#share-card-action',
    });

    app.component('message-card-action', {
        extends: buttonCardAction,
        template: buttonCardActionTemplateName,
        methods: {
            onActionClick() {
                this.setPrimary();
            },
        },
    });

    app.component('rate-card-action', {
        extends: cardActionBase,
        template: '#rate-card-action',
    });

    app.component('message-primary-action', {
        extends: primaryTextActionBase,
        template: primaryTextAction,
        methods: {
            async onSubmit(message) {
                const chat = window.chat;
                if (chat) {
                    chat.onConversationOpening = async (convId) => {
                        chat.currentMessage = message;
                        await chat.sendMessage(convId, false, false);
                        chat.currentConversation = null;
                        chat.onConversationOpening = async () => {
                        };
                    };

                    await chat.initiateConversation(this.targetId, this.targetType);
                    this.clearTextarea();
                }
            },
        },
    });

    app.component('comment-primary-action', {
        extends: primaryTextActionBase,
        template: primaryTextAction,
        data() {
            return {
                validationExpression: `min: 3, max: 500`,
            };
        },
        methods: {
            async onSubmit(message) {
                const validator = this.$refs.validator;
                const result = await validator.validate();
                if (!result.valid) {
                    this.showErrors = true;
                    return;
                }

                try {
                    await axios.post(this.url, {body: message, targetId: this.targetId, commentType: this.targetType});
                    if (this.successHeader && this.successMessage) {
                        globalActions.openInfoModal(this.successHeader, this.successMessage);
                        this.clearTextarea();
                    }
                } catch (err) {
                    console.error(err);
                    globalActions.handleAjaxErrors(err);
                }
            },
        },
    });

    app.component('link-card-action', {
        extends: buttonCardAction,
        template: buttonCardActionTemplateName,
        methods: {
            onActionClick() {
                window.location = this.url;
            },
        },
    });

    app.component('post-card-action', {
        extends: buttonCardAction,
        template: buttonCardActionTemplateName,
        methods: {
            onActionClick() {
                axios.post(this.url, this.data)
                    .then((response) => {
                        window.location = `${response.data}`;
                    })
                    .catch((result) => globalActions.handleAjaxErrors(result.response));
            },
        },
    });

    app.component('dealcompleted-card-action', {
        extends: buttonCardAction,
        template: buttonCardActionTemplateName,
        methods: {
            onActionClick() {
                advertActions.openCascadeModal(this.modelValue, CascadeName.DEAL_COMPLETED_CASCADE);
            },
        },
    });

    app.component('copyas-card-action', {
        extends: cardActionBase,
        template: '#copyas-card-action',
        methods: {
            onActionClick() {
                advertActions.openCopyAsModal(this.modelValue);
            },
        },
    });

    app.component('delete-card-action', {
        extends: buttonCardAction,
        template: buttonCardActionTemplateName,
        methods: {
            onActionClick() {
                if (this.modelValue.hasActiveOrders) {
                    advertActions.openCascadeModal(this.modelValue, CascadeName.DELETE_WITH_ORDER_CASCADE);
                } else {
                    advertActions.openCascadeModal(this.modelValue, CascadeName.DELETE_CASCADE);
                }
            },
        },
    });

    app.component('raiserelevance-card-action', {
        extends: buttonCardAction,
        template: buttonCardActionTemplateName,
        methods: {
            onActionClick() {
                axios.post(this.modelValue.url)
                    .then(() => {
                        this.$root.showUpdatedModal = true;
                        this.$root.createOrderUrl = this.modelValue.createOrderUrl;
                    })
                    .catch((result) => globalActions.handleAjaxErrors(result.response));
            },
        },
    });
}

export default initCardActionComponents;
